import React, { useState, useEffect, useRef } from "react";
import {
    IonPage,
    IonContent,
    IonButton,
    IonCard,
    IonCardContent,
    IonImg,
    IonRadioGroup,
    IonRadio,
    IonItem,
    IonLabel,
    IonModal,
} from "@ionic/react";
import { Helmet } from "react-helmet";

// Define types for questions
type QuestionType = "picture" | "multipleChoice" | "trueFalse";

interface PictureQuestion {
    question: string;
    options: { src: string; answer: boolean }[];
}

interface MultipleChoiceQuestion {
    question: string;
    options: string[];
    correct: string;
}

interface TrueFalseQuestion {
    question: string;
    correct: boolean;
}


const TrafficSafetyQuiz: React.FC = () => {
    const [currentQuestion, setCurrentQuestion] = useState<PictureQuestion | MultipleChoiceQuestion | TrueFalseQuestion | null>(null);
    const [selectedOption, setSelectedOption] = useState<string | boolean | null>(null);
    const [showResult, setShowResult] = useState<boolean>(false);
    const [showImage, setShowImage] = useState(false);
    const [isCorrect, setIsCorrect] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);

    // Initialize the state with an empty object that matches the required structure
    const [questionBank, setQuestionBank] = useState<Record<QuestionType, (PictureQuestion | MultipleChoiceQuestion | TrueFalseQuestion)[]>>({
        picture: [],
        multipleChoice: [],
        trueFalse: []
    });

    const fetchQuestions = async () => {
        try {
            let multipleChoiceQuestion: MultipleChoiceQuestion[] = [];
            let trueFalseQuestion: TrueFalseQuestion[] = [];
            let pictureQuestion: PictureQuestion[] = [];

            if (process.env.REACT_APP_DEVELOPMENT !== 'WEB') {

                const response = await fetch(process.env.REACT_APP_API_URL + 'TrafficRulesTrueFalse.json');
                trueFalseQuestion = await response.json();

                const response1 = await fetch(process.env.REACT_APP_API_URL + 'TrafficRulesMultipleChoiceQuestion.json');
                multipleChoiceQuestion = await response1.json();

                const response2 = await fetch(process.env.REACT_APP_API_URL + 'TrafficRulesPicture.json');
                pictureQuestion = await response2.json();

            } else {
                const module = await import('../../../datas/TrafficRulesTrueFalse.json');
                trueFalseQuestion = module.default;

                const module1 = await import('../../../datas/TrafficRulesMultipleChoiceQuestion.json');
                multipleChoiceQuestion = module1.default;

                const module2 = await import('../../../datas/TrafficRulesPicture.json');
                pictureQuestion = module2.default;
            }
            const questionBankList: Record<QuestionType, (PictureQuestion | MultipleChoiceQuestion | TrueFalseQuestion)[]> = {
                picture: pictureQuestion,
                multipleChoice: multipleChoiceQuestion,
                trueFalse: trueFalseQuestion,
            };
            setQuestionBank(questionBankList);
        } catch (error) {
            console.error("Error loading questions:", error);
        }
    };

    useEffect(() => {
        fetchQuestions();
        document.title = "Traffic Rules and Road Safety Worksheets for LKG | Mama’s Guidance"; // Set page title
    }, []);

    // Use an effect to pick a random question when the question bank is updated
    useEffect(() => {
        if (questionBank.picture.length > 0 || questionBank.multipleChoice.length > 0 || questionBank.trueFalse.length > 0) {
            getRandomQuestion();
        }
    }, [questionBank]); // Trigger when questionBank changes

    const getRandomQuestion = () => {
        const questionTypes = Object.keys(questionBank) as QuestionType[];
        const randomType = questionTypes[Math.floor(Math.random() * questionTypes.length)];
        const randomQuestion = questionBank[randomType][Math.floor(Math.random() * questionBank[randomType].length)];
        setCurrentQuestion(randomQuestion);
        setSelectedOption(null);
    };

    const showNextQuestion = () => {
        setShowImage(true);
        setTimeout(() => {
            setShowImage(false);
            setSelectedOption(null);
            getRandomQuestion();
            setIsCorrect(false)
        }, 5000);
    }

    const handleAnswerSubmit = (option: any) => {
        setSelectedOption(option);
        console.log(currentQuestion, option)

        if (!currentQuestion) return;

        if ("correct" in currentQuestion) {
            // For multiple choice and true/false questions
            if (option === currentQuestion.correct) {
                showNextQuestion();
                setIsCorrect(true);
            }
        } else if ("options" in currentQuestion) {
            // For picture-based questions
            if ((currentQuestion as PictureQuestion).options.find((opt) => opt.src === option)?.answer) {
                showNextQuestion();
                setIsCorrect(true);
            }
        }
    };

    const renderQuestion = () => {
        if (!currentQuestion) return <h3>Loading question...</h3>;

        if ("options" in currentQuestion) {
            if (typeof currentQuestion.options[0] === "object") {
                // Picture-based question
                return (
                    <>
                        <h3 className="mb-5">{currentQuestion.question}</h3>
                        <div className="row">
                            {(currentQuestion as PictureQuestion).options.map((option, index) => (
                                <div
                                    className={`col-6 col-md-3 mb-5 `}
                                    key={index}>
                                    <img
                                        src={process.env.REACT_APP_API_URL + option.src}
                                        onClick={() => handleAnswerSubmit(option.src)}
                                        className={`livingImage img-thumbnail ${selectedOption === option.src
                                            ? isCorrect === true
                                                ? "correct"
                                                : "errorImgItems"
                                            : ""
                                            }`}
                                        style={{
                                            cursor: "pointer",
                                        }}
                                    />
                                </div>
                            ))}
                        </div>
                    </>
                );
            } else {
                // Multiple-choice question
                return (
                    <>
                        <h3>{currentQuestion.question}</h3>
                        <IonRadioGroup onIonChange={(e) => handleAnswerSubmit(e.detail.value)}>
                            {(currentQuestion as MultipleChoiceQuestion).options.map((option, index) => (
                                <IonItem
                                    className={`grid-item ${selectedOption === option
                                        ? isCorrect === true
                                            ? "correct"
                                            : "errorItems"
                                        : ""
                                        }`}
                                    key={index}>
                                    <IonRadio slot="start" value={option} />
                                    <IonLabel>{option}</IonLabel>
                                </IonItem>
                            ))}
                        </IonRadioGroup>
                    </>
                );
            }
        } else {
            // True/False question
            return (
                <>
                    <h3>{currentQuestion.question}</h3>
                    <IonRadioGroup onIonChange={(e) => handleAnswerSubmit(e.detail.value!)}>
                        <IonItem
                            className={`grid-item ${selectedOption === true
                                ? isCorrect === true
                                    ? "correct"
                                    : "errorItems"
                                : ""
                                }`}>
                            <IonRadio slot="start" value={true} />
                            <IonLabel>True</IonLabel>
                        </IonItem>
                        <IonItem
                            className={`grid-item ${selectedOption === false
                                ? isCorrect === true
                                    ? "correct"
                                    : "errorItems"
                                : ""
                                }`}>
                            <IonRadio slot="start" value={false} />
                            <IonLabel>False</IonLabel>
                        </IonItem>
                    </IonRadioGroup>
                </>
            );
        }
    };

    return (
        <>
            <Helmet>
                <meta name="description" content="Teach your LKG child about basic traffic rules and road safety with fun and interactive worksheets. Help them learn the importance of safe walking, crossing, and signals." />
            </Helmet>
            {showImage === false ? (
                <>
                    <div className="contentPart showCenter fillPreposition additionWord">
                        <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                        <IonModal
                            ref={modal}
                            trigger="open-modal"
                            initialBreakpoint={1}
                            breakpoints={[0, 0.5, 0.75, 1]}
                            handleBehavior="cycle"
                            backdropDismiss={false}  // ✅ Prevents click-to-close
                            className="full-page-modal"
                        >
                            <IonContent className="ion-padding">
                                <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                <h1>Traffic Rules and Road Safety</h1>

                                <p>
                                    Teaching children about <strong>traffic rules and road safety</strong> from an early age is crucial. These LKG-level worksheets are designed to help young learners recognize road signs, follow basic pedestrian rules, and understand the importance of staying safe near roads and vehicles. Through colorful visuals and fun activities, children will build awareness that will last a lifetime.
                                </p>

                                <h2>🚦 Why Teach Traffic Rules in LKG?</h2>
                                <p>
                                    Children are exposed to roads, vehicles, and pedestrian environments regularly. Learning how to cross safely, recognize traffic lights, and follow rules empowers them with early-life safety skills.
                                </p>

                                <h2>🎯 Learning Objectives</h2>
                                <ul>
                                    <li>Identify basic traffic lights: red, yellow, green</li>
                                    <li>Recognize common road signs (zebra crossing, stop, no horn, etc.)</li>
                                    <li>Understand safe crossing at zebra crossings</li>
                                    <li>Learn how to walk on footpaths and obey traffic signals</li>
                                    <li>Develop early sense of responsibility and alertness</li>
                                </ul>

                                <h2>📋 Activities Included in the Worksheets</h2>
                                <ul>
                                    <li>Match the traffic light color with its meaning</li>
                                    <li>Circle the safe actions and cross the unsafe ones</li>
                                    <li>Identify the correct road sign</li>
                                    <li>Color the traffic signal and zebra crossing</li>
                                    <li>Picture-based questions about road behavior</li>
                                </ul>

                                <h2>👨‍👩‍👧 Tips for Parents and Teachers</h2>
                                <ul>
                                    <li>Practice role-play of crossing the road at home using toys and cardboard signs</li>
                                    <li>Take short walks and explain road signs you see on the way</li>
                                    <li>Use stories or animated videos about traffic rules to reinforce concepts</li>
                                    <li>Remind children to always hold an adult’s hand while crossing the road</li>
                                </ul>



                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/lkg/environmental-science/animal"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Animal</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/lkg/environmental-science/living-non-living"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Living & Non Living Things</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🌟 Mama’s Guidance Tip</h2>
                                <p>
                                    Every outing is a chance to teach road safety. Turn your walks into safety lessons – stop, look, and walk together!
                                </p>

                                <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                            </IonContent>
                        </IonModal>
                        <article className="visually-hidden">
                            <h1>Traffic Rules and Road Safety</h1>

                            <p>
                                Teaching children about <strong>traffic rules and road safety</strong> from an early age is crucial. These LKG-level worksheets are designed to help young learners recognize road signs, follow basic pedestrian rules, and understand the importance of staying safe near roads and vehicles. Through colorful visuals and fun activities, children will build awareness that will last a lifetime.
                            </p>

                            <h2>🚦 Why Teach Traffic Rules in LKG?</h2>
                            <p>
                                Children are exposed to roads, vehicles, and pedestrian environments regularly. Learning how to cross safely, recognize traffic lights, and follow rules empowers them with early-life safety skills.
                            </p>

                            <h2>🎯 Learning Objectives</h2>
                            <ul>
                                <li>Identify basic traffic lights: red, yellow, green</li>
                                <li>Recognize common road signs (zebra crossing, stop, no horn, etc.)</li>
                                <li>Understand safe crossing at zebra crossings</li>
                                <li>Learn how to walk on footpaths and obey traffic signals</li>
                                <li>Develop early sense of responsibility and alertness</li>
                            </ul>

                            <h2>📋 Activities Included in the Worksheets</h2>
                            <ul>
                                <li>Match the traffic light color with its meaning</li>
                                <li>Circle the safe actions and cross the unsafe ones</li>
                                <li>Identify the correct road sign</li>
                                <li>Color the traffic signal and zebra crossing</li>
                                <li>Picture-based questions about road behavior</li>
                            </ul>

                            <h2>👨‍👩‍👧 Tips for Parents and Teachers</h2>
                            <ul>
                                <li>Practice role-play of crossing the road at home using toys and cardboard signs</li>
                                <li>Take short walks and explain road signs you see on the way</li>
                                <li>Use stories or animated videos about traffic rules to reinforce concepts</li>
                                <li>Remind children to always hold an adult’s hand while crossing the road</li>
                            </ul>



                            <h2>🔗 Related Topics</h2>
                            <div>
                                <IonItem routerLink="/lkg/environmental-science/animal"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Animal</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/lkg/environmental-science/living-non-living"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Living & Non Living Things</IonLabel>
                                </IonItem>
                            </div>

                            <h2>🌟 Mama’s Guidance Tip</h2>
                            <p>
                                Every outing is a chance to teach road safety. Turn your walks into safety lessons – stop, look, and walk together!
                            </p>
                        </article>
                        {renderQuestion()}
                    </div>
                </>
            ) : (
                <>
                    <div className="contentPart showMiddle">
                        <img
                            style={{ height: "150px" }}
                            className="img-fluid"
                            src={process.env.REACT_APP_WELDONE_LINK}
                        />
                    </div>
                </>
            )}
        </>
    );
};

export default TrafficSafetyQuiz;
